import { render } from "preact";
import { signal } from "@preact/signals";
import type { AtmosphereRating } from "../atmosphere.ts";
import {
  type Appearance,
  appearanceLabel,
  FREE_APPEARANCES,
  type Material,
} from "../appearance.ts";
import type { CriterionResult, Judgement } from "../judge.ts";
import type { Tag, Tags } from "../tags.ts";
import { challengeFor, meetsMastery } from "../progression.ts";
import styles from "./result.module.css";

/**
 * How far a look at the piece by the VLM has got: not asked for yet (or not possible here),
 * the model on its way or looking, and what it saw.
 */
export type Looking<T> =
  | { state: "off" | "unsupported" | "looking" | "failed" }
  | { state: "loading"; share: number }
  | { state: "rated"; seen: T };

/** The request's air (see atmosphere.ts), as far as looking at it has got. */
export type AtmosphereView = Looking<AtmosphereRating> & { label: string };

/** What the result panel shows after submitting: the game writes it, the panel follows. */
export const result = {
  open: signal(false),
  /** Kneaded freely: nothing to judge, only how to finish it. */
  free: signal(false),
  /** The materials a piece kneaded freely can be finished as (see freeKneading). */
  materials: signal<ReadonlySet<Material>>(new Set(["ceramic"])),
  judgement: signal<Judgement | null>(null),
  /** Null when the letter asks for no air. */
  atmosphere: signal<AtmosphereView | null>(null),
  /** How a piece kneaded freely comes across (see tags.ts), as far as looking at it has got. */
  tags: signal<Looking<Tags> | null>(null),
  /** The morph between the clay (0) and the target (1), played by itself at first. */
  morph: signal(0),
  /** Frames of the kneading so far, the one shown, and the strokes counted by then. */
  frames: signal(1),
  frame: signal(0),
  frameStrokes: signal(0),
  ghost: signal(false),
  requestId: signal(""),
  strokes: signal(0),
};

/** What the result panel asks of the game. */
export interface ResultActions {
  /** The player takes over the morph from the auto-play. */
  setMorph(t: number): void;
  showFrame(k: number): void;
  setGhost(on: boolean): void;
  /**
   * The player lets the piece be looked at (its air, or how a piece kneaded freely comes
   * across; from now on, once it has been seen), or asks again after a failure.
   */
  look(): void;
  rework(): void;
  deliver(): void;
  /** A piece kneaded freely, finished as chosen. */
  finish(appearance: Appearance): void;
}

/** The judgement, a way to compare with the target, and what to do next. */
export function mountResult(root: HTMLElement, actions: ResultActions): void {
  render(<ResultPanel actions={actions} />, root);
}

function ResultPanel({ actions }: { actions: ResultActions }) {
  if (!result.open.value) return null;
  if (result.free.value) return <FinishPanel actions={actions} />;
  const j = result.judgement.value;
  if (!j) return null;
  const challenge = challengeFor(result.requestId.value);
  return (
    <section class={`panel ${styles.result}`}>
      <div class={styles.head}>
        <span class={styles.rank} data-rank={j.rank}>{j.rank}</span>
        <div>
          <p class={styles.total}>
            {j.total}
            <small>点</small>
          </p>
          <p class={styles.breakdown}>基本 {j.base} ＋ ボーナス {j.bonus}</p>
          <p class={styles.title}>{j.title}</p>
        </div>
      </div>
      {challenge && (
        <div class={styles.challenge} role="status">
          <b>
            {j.total >= 70
              ? "このまま納品するとお題達成！"
              : "お題の達成まで、B以上（70点）を目指そう"}
          </b>
          <p>納品して初めて記録されます。手数は自由、何度でもこね直せます。</p>
          <p>
            {meetsMastery(challenge.request, j.total, result.strokes.value)
              ? "腕試しの条件も達成！"
              : `任意の腕試し: A以上（100点）・${challenge.maxStrokes}手以内（今${result.strokes.value}手）`}
          </p>
        </div>
      )}
      <p class={styles.groupTitle}>基本（100点満点）</p>
      <Criteria items={j.main} />
      <p class={styles.groupTitle}>ボーナス</p>
      <Criteria items={j.bonuses} bonus />
      <p class={styles.good}>
        {j.good.length
          ? `ここが良い！ ${j.good.map((c) => c.comment).join(" ")}`
          : "まずは大胆にこねてみよう！"}
      </p>
      {j.hint && <p class={styles.hint}>次は… {j.hint}</p>}
      {result.atmosphere.value && (
        <>
          <p class={styles.groupTitle}>雰囲気（点数には入りません）</p>
          <AtmosphereRow view={result.atmosphere.value} actions={actions} />
        </>
      )}
      <label class={styles.morph}>
        <span>あなた</span>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={result.morph}
          aria-label="あなたとおてほんの間"
          onInput={(e) => actions.setMorph(Number(e.currentTarget.value))}
        />
        <span>おてほん</span>
      </label>
      <p class={styles.groupTitle}>こね手順</p>
      <label class={styles.timeline}>
        <span>はじめ</span>
        <input
          type="range"
          min="0"
          max={result.frames.value - 1}
          step="1"
          value={result.frame}
          aria-label="こねた手順"
          onInput={(e) => actions.showFrame(Number(e.currentTarget.value))}
        />
        <span>{result.frameStrokes}手</span>
      </label>
      <label class={styles.check}>
        <input
          type="checkbox"
          checked={result.ghost}
          onChange={(e) => actions.setGhost(e.currentTarget.checked)}
        />{" "}
        目標と重ねて見る
      </label>
      <div class={`row ${styles.actions}`}>
        <button type="button" class="reset" onClick={() => actions.rework()}>こね直す</button>
        <button type="button" class="submit" onClick={() => actions.deliver()}>納品する</button>
      </div>
    </section>
  );
}

/** How a piece kneaded freely can be finished, as its buttons say. */
const APPEARANCES = FREE_APPEARANCES.map((appearance) =>
  [appearance, appearanceLabel(appearance)] as const
);

/**
 * A freely kneaded piece can use each open material's initial finish, or go back to kneading.
 */
function FinishPanel({ actions }: { actions: ResultActions }) {
  return (
    <section class={`panel ${styles.result} ${styles.finish}`}>
      <p class={styles.finishTitle}>できあがり！</p>
      {result.tags.value && (
        <div class={styles.seenSlot}>
          <TagsRow view={result.tags.value} actions={actions} />
        </div>
      )}
      <p class={styles.finishLead}>どの材質で作る？</p>
      <div class={styles.finishes}>
        {APPEARANCES.filter(([a]) => result.materials.value.has(a.material)).map((
          [appearance, name],
        ) => (
          <button
            key={`${appearance.material}:${appearance.finish}`}
            type="button"
            class="submit"
            onClick={() => actions.finish(appearance)}
          >
            {name}
          </button>
        ))}
      </div>
      {result.materials.value.size < APPEARANCES.length && (
        <p class={styles.finishNote}>依頼をクリアすると、その材質でも作れるようになります</p>
      )}
      <div class={`row ${styles.actions}`}>
        <button type="button" class="reset" onClick={() => actions.rework()}>こね直す</button>
      </div>
    </section>
  );
}

/**
 * How far a look at the piece has got, while it has not seen it (or cannot): a gauge while
 * the model loads, and the word and the button under it.
 */
function progressOf(view: Looking<unknown>) {
  return {
    fill: view.state === "loading" ? view.share : null,
    status: view.state === "loading"
      ? `${Math.floor(view.share * 100)}%`
      : view.state === "looking"
      ? "見ています…"
      : "",
    note: {
      off: "画像を読む AI に、この端末の中で見てもらえます（初めては約 850MB を読み込みます）",
      unsupported: "この端末では見られません（WebGPU が必要です）",
      loading: "こねこ準備中…",
      looking: "こねこがじっと見ています",
      failed: "うまく見られませんでした",
      rated: "こねこの見立てです",
    }[view.state],
    ask: view.state === "off" ? "見てもらう" : view.state === "failed" ? "もう一度" : null,
  };
}

/** The word under a look at the piece, with the button to let it look (or look again). */
function LookFoot(
  { note, ask, actions }: { note: string; ask: string | null; actions: ResultActions },
) {
  return (
    <div class={styles.atmosphereFoot}>
      <p class={styles.atmosphereNote}>{note}</p>
      {ask && (
        <button type="button" class={styles.look} onClick={() => actions.look()}>
          {ask}
        </button>
      )}
    </div>
  );
}

/**
 * The air the letter hopes for: how strongly it comes across, as a gauge with its level in
 * words, or how far looking at it has got.
 */
function AtmosphereRow({ view, actions }: { view: AtmosphereView; actions: ResultActions }) {
  const progress = progressOf(view);
  const fill = view.state === "rated" ? view.seen.strength : progress.fill;
  const status = view.state === "rated" ? view.seen.text : progress.status;
  return (
    <div class={styles.atmosphere}>
      <div class={styles.criterionHead}>
        <span>{view.label}</span>
        <span>{status}</span>
      </div>
      {fill !== null && (
        <div class={styles.meter}>
          <div class={styles.fill} style={{ width: `${fill * 100}%` }} />
        </div>
      )}
      <LookFoot note={progress.note} ask={progress.ask} actions={actions} />
    </div>
  );
}

/** How strongly a label is put on, in words before it: faintly, plainly, strongly. */
const TAG_DEGREES = ["少し", "", "かなり"];

/**
 * How a piece kneaded freely comes across: its labels (its airs, what it looks like and its
 * shape), or how far looking at it has got.
 */
function TagsRow({ view, actions }: { view: Looking<Tags>; actions: ResultActions }) {
  const progress = progressOf(view);
  const chip = (kind: string, { def, level }: Tag) => (
    <li key={def.label} class={styles.tag} data-kind={kind} data-level={level}>
      {level !== 1 && <small>{TAG_DEGREES[level]}</small>}
      {def.label}
    </li>
  );
  return (
    <div class={`${styles.atmosphere} ${styles.seen}`}>
      <div class={styles.criterionHead}>
        <span>こねこの見立て</span>
        <span>{progress.status}</span>
      </div>
      {progress.fill !== null && (
        <div class={styles.meter}>
          <div class={styles.fill} style={{ width: `${progress.fill * 100}%` }} />
        </div>
      )}
      {view.state === "rated"
        ? (
          <ul class={styles.tags}>
            {view.seen.airs.map((t) => chip("air", t))}
            {chip("look", view.seen.look)}
            {chip("form", view.seen.form)}
          </ul>
        )
        : <LookFoot note={progress.note} ask={progress.ask} actions={actions} />}
    </div>
  );
}

/** Each criterion's points, as a gauge, with a word on it. */
function Criteria({ items, bonus = false }: { items: CriterionResult[]; bonus?: boolean }) {
  return (
    <ul class={bonus ? `${styles.criteria} ${styles.bonus}` : styles.criteria}>
      {items.map((c) => (
        <li key={c.label}>
          <div class={styles.criterionHead}>
            <span>{c.label}</span>
            <span>
              {bonus ? `+${c.points}` : c.points}
              <small>/ {c.max}</small>
            </span>
          </div>
          <div class={styles.meter}>
            <div class={styles.fill} style={{ width: `${(c.points / c.max) * 100}%` }} />
          </div>
          <p class={styles.comment}>{c.comment}</p>
        </li>
      ))}
    </ul>
  );
}
